<template>
    <div class="main-container overflow-x-auto">
        <div class="overflow-auto pa-5" style="grid-area: search">
            <h4 class="mb-2">{{ $t('mag_addr_srch_title') }}</h4>
            <p style="min-height: 50px">{{ $t('mag_addr_srch_txt') }}</p>
            <!-- search list -->
            <BaseRemoteList
                dense
                color="var(--tgk-grey)"
                session-key="magazin-address-search-list"
                :collection-class="AddressCollection"
                :extra-params="queryParams"
                :headers="searchHeaders"
                :filters="searchFilters"
                :options="{ sortBy: ['title'], sortDesc: [false] }"
                hide-default-footer
                hide-top-toolbar
                :new-btn="false"
                :reload-btn="false"
                disable-pagination
                @loading="loadingSearch = $event"
                @edit-item="onEdit"
            >
                <template #toolbar-items>
                    <span></span>
                </template>
                <template #[`item.assignBtn`]="{ item }">
                    <icon-btn
                        icon-cls="arrow-bigright"
                        icon-color="action"
                        plain
                        fab
                        :tooltip="$t('assign')"
                        @click="assignAddress(item)"
                    />
                </template>
                <template #[`item.title`]="{ item }">
                    <list-item-title :item="item" />
                </template>
                <template #[`item.adresse_typ`]="{ item }">
                    <div class="d-flex">
                        <div class="d-flex flex-column mr-2">
                            <TypIcon :address-record="item" />

                            <KpIcon v-if="!item.origin" icon-cls="ownnotimported" color="grey" />
                            <KpIcon v-if="item.origin === 'guidle'" icon-cls="guidleimport" color="grey" />
                            <KpIcon v-if="item.origin === 'eventfrog'" icon-cls="eventfrog" color="grey" />
                        </div>
                        <address class="">
                            {{ item.address }}<br />
                            {{ item.plz }} {{ item.city }}<br />
                        </address>
                    </div>
                </template>

                <template #after-query-field>
                    <div
                        :class="{
                            'ml-1': true,
                            'erw-suche-indicator': Object.keys(extendedSearchParams || {}).length > 0,
                        }"
                    >
                        <IconBtn
                            color="grey"
                            :disabled="loadingSearch"
                            icon-cls="advancedsearch"
                            icon-color="white"
                            icon-disabled-color="grey"
                            :tooltip="$t('erw_suche')"
                            @click="showErwSuche = true"
                        />
                    </div>
                </template>
            </BaseRemoteList>
        </div>

        <div class="overflow-auto assigned-panel pt-5 pl-5 pr-5" style="grid-area: assigned">
            <h4 class="mb-2">{{ $t('mag_addr_assg_title') }}</h4>
            <p style="min-height: 50px">{{ $t('mag_addr_assg_txt') }}</p>
            <!-- assigned list -->
            <BaseList
                dense
                color="var(--tgk-grey)"
                session-key="magazin-address-assigned-list"
                :loading="loadingAssigned"
                :items="assignedAddressCollection.models"
                :headers="assignedHeaders"
                hide-default-footer
                hide-top-toolbar
                :reload-btn="false"
                :new-btn="false"
                :query-field="false"
                disable-pagination
                @edit-item="onEdit"
                @delete-item="onDeleteAssigned"
                @reload="loadAssignedAddresses()"
            >
                <template #toolbar-items>
                    <span></span>
                </template>
                <template #[`item.title`]="{ item }">
                    <list-item-title :item="item" />
                </template>
                <template #[`item.adresse_typ`]="{ item }">
                    <div class="d-flex">
                        <div class="d-flex flex-column mr-2">
                            <TypIcon :address-record="item" />

                            <KpIcon v-if="!item.origin" icon-cls="ownnotimported" color="grey" />
                            <KpIcon v-if="item.origin === 'guidle'" icon-cls="guidleimport" color="grey" />
                            <KpIcon v-if="item.origin === 'eventfrog'" icon-cls="eventfrog" color="grey" />
                        </div>
                        <address class="">
                            {{ item.address }}<br />
                            {{ item.plz }} {{ item.city }}<br />
                        </address>
                    </div>
                </template>
            </BaseList>
        </div>
        <!-- enhanced search btn -->
        <v-navigation-drawer v-model="showErwSuche" absolute bottom temporary width="530px" color="kp-light-panel-bg">
            <v-toolbar dense class="kp-sublime-toolbar kp-root-panel-bg" elevation="0">
                <v-toolbar-title>{{ $t('erw_suche') }}</v-toolbar-title>
                <v-spacer />
                <IconBtn icon-cls="x" icon-color="grey" @click="showErwSuche = false" />
            </v-toolbar>
            <AddressEnhancedSearch
                ref="search"
                style="grid-area: search; overflow: auto"
                :initial-params="extendedSearchParams"
                @search="onEnhancedSearch"
                @reset="resetEnhancedSearch"
            />
        </v-navigation-drawer>
    </div>
</template>

<script>
import { instance as api } from '@/helpers/api';
import { mapActions } from 'vuex';
import { errToStr } from '@/helpers/tools';
import AddressEnhancedSearch from '@/components/address/EnhancedSearch.vue';
import ListItemTitle from '@/components/address/list-item-title.vue';
import TypIcon from '@/components/address/TypIcon.vue';
import sessionStorageMixin from '@/mixins/session-storage';
import { AddressCollection } from '@/models/Address';
import { Magazin } from '@/models/Magazin';

export default {
    name: 'MagazinEditAddresses',
    components: {
        AddressEnhancedSearch,
        TypIcon,
        ListItemTitle,
    },
    mixins: [sessionStorageMixin],
    props: {
        record: {
            type: Magazin,
            required: true,
        },
    },
    data() {
        this.sessionKey = 'magazin-edit-address';
        const assignedAddressCollection = new AddressCollection();

        return {
            loadingAssigned: false,
            loadingSearch: false,
            showErwSuche: false,
            extendedSearchParams: this.getSessionValue('extendedSearchParams') || {},
            assignedAddressCollection: assignedAddressCollection,
            assignedItemToDelete: null,
            assignedHeaders: [
                { text: this.$t('titel'), sortable: true, value: 'title' },
                { text: this.$t('adresse_typ'), value: 'adresse_typ', sortable: false },
                { sortable: false, value: 'actions' },
            ],
            searchFilters: [
                { property: 'is_visible', value: true },
                { property: 'status', value: 'in_union' },
            ],
            searchHeaders: [
                { text: this.$t('titel'), sortable: true, value: 'title' },
                { text: this.$t('adresse_typ'), value: 'adresse_typ', sortable: false },
                { text: this.$t('assign'), sortable: false, value: 'assignBtn' },
            ],
        };
    },

    computed: {
        queryParams() {
            return {
                enhancedFilters: this.extendedSearchParams,
            };
        },
    },
    created() {
        this.AddressCollection = AddressCollection;
    },

    mounted() {
        this.loadAssignedAddresses();
    },

    methods: {
        ...mapActions(['runAsync']),
        async loadAssignedAddresses() {
            this.loadingAssigned = true;
            this.assignedAddressCollection.addExtraParam('magazin_id', this.record ? this.record.id : -1);
            await this.assignedAddressCollection.query();
            this.loadingAssigned = false;
        },

        onEnhancedSearch(params) {
            this.extendedSearchParams = { ...params };
            this.showErwSuche = false;
        },
        resetEnhancedSearch() {
            this.extendedSearchParams = {};
            this.showErwSuche = false;
        },

        onEdit(address) {
            this.$router.push({ name: 'address-edit', params: { id: address.id } });
        },

        async assignAddress(address) {
            if (address) {
                try {
                    this.loadingAssigned = true;
                    await this.runAsync(() =>
                        api().post('/magazinAddress/createLink', {
                            magazin_id: this.record.id,
                            address_id: address.id,
                        }),
                    );
                    this.loadAssignedAddresses();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingAssigned = false;
                }
            }
        },

        async onDeleteAssigned(address) {
            if (address) {
                try {
                    this.loadingAssigned = true;
                    await this.runAsync(() =>
                        api().post('/magazinAddress/removeLinksForMagazin', {
                            magazin_id: this.record.id,
                            address_ids: JSON.stringify([address.id]),
                        }),
                    );
                    this.loadAssignedAddresses();
                } catch (e) {
                    this.$toast({ message: errToStr(e), color: 'error' });
                } finally {
                    this.loadingAssigned = false;
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.main-container {
    display: grid;
    gap: 20px;
    grid-template-columns: minmax(400px, 5fr) minmax(400px, 4fr);
    grid-template-rows: auto;
    grid-template-areas: 'search assigned';
}
.assigned-panel {
    background-color: var(--toolbar-bg-color);
}
</style>
